/*
  © 2024 Jhango
  https://www.jhango.com
*/

class BundleBuilder extends HTMLElement {
  constructor () {
    super()

    this.bundleContents = JSON.parse(localStorage.getItem('ks-bundle-contents')) || []
    this.selectedTier = Number(localStorage.getItem('ks-bundle-selected-tier')) || null
    this.card = this.querySelector('#collection-bundle-card')

    this.buildBundle()
    this.calcPrices()
    this.handleCartStickyPosition()
    this.animateBtnBundleCheckout()
    this.initStickyMobileCard()
    this.initMobilePopup()

    this.querySelectorAll('.product-card form').forEach(form => {
      form.addEventListener('submit', (e) => e.preventDefault())
    })

    this.querySelectorAll('[data-bundle-qty-change]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.changeQty(btn.closest('.product-card'), Number(btn.dataset.bundleQtyChange))
      })
    })

    this.querySelectorAll('[data-bundle-qty-input]').forEach(input => {
      input.addEventListener('change', () => this.onQtyInput(input))
    })

    this.querySelectorAll('.product-card select[name="id"').forEach(select => {
      select.addEventListener('change', () => this.onChangeProductCardVariant(select))
    })

    this.addEventListener('click', (e) => {
      const btn = e.target.closest('.collection-bundle-card-list .btn-close')
      if (btn) this.removeFromBundle(btn)
    })

    this.querySelectorAll('.btn-bundle-checkout').forEach(btn => {
      btn.addEventListener('click', () => this.handleCheckout(btn))
    })

    this.querySelectorAll('[data-tier-summary]').forEach(btn => {
      btn.addEventListener('click', () => this.toggleTier(Number(btn.dataset.tierQty)))
    })
  }

  /* Variant currently selected on a product card. */
  selectedOption (card) {
    const select = card.querySelector('select[name="id"]')
    return select.options[select.selectedIndex]
  }

  /* How many units of a variant are already in the bundle. */
  qtyInBundle (variantId) {
    return this.bundleContents.filter(elem => elem.variant_id === Number(variantId)).length
  }

  /* Highest tier configured — the last slot rendered in the bundle card. */
  maxQty () {
    return window.theme.bundle_builder_tiers[window.theme.bundle_builder_tiers.length - 1].qty
  }

  /* Highest tier the bundle currently qualifies for (null while below tier 1). */
  appliedTier () {
    let applied = null

    window.theme.bundle_builder_tiers.forEach(tier => {
      if (tier.qty <= this.bundleContents.length) applied = tier.qty
    })

    return applied
  }

  /* Lowest tier the bundle has not reached yet. */
  nextTier () {
    const next = window.theme.bundle_builder_tiers
      .find(tier => tier.qty > this.bundleContents.length)

    return next ? next.qty : null
  }

  /* Pick a tier as the goal, or clear it by picking it again. */
  toggleTier (qty) {
    this.selectedTier = this.selectedTier === qty ? null : qty

    if (this.selectedTier) {
      localStorage.setItem('ks-bundle-selected-tier', this.selectedTier)
    } else {
      localStorage.removeItem('ks-bundle-selected-tier')
    }

    this.buildBundle()
    this.calcPrices()
  }

  /* Highlight the tier in play: the chosen goal, the tier already applied,
     or — before any tier is reached — the one that can be applied next. */
  updateTierSummary () {
    const strings = window.theme.bundle_builder_strings || {}
    const applied = this.appliedTier()
    const next = this.nextTier()
    /* Progress is automatic: once a tier is reached the highlight moves on to
       the next one, so nothing has to be clicked to keep adding products. */
    const highlighted = this.selectedTier || next || applied

    this.querySelectorAll('[data-tier-summary]').forEach(btn => {
      const qty = Number(btn.dataset.tierQty)
      const isSelected = this.selectedTier === qty
      const status = btn.querySelector('[data-tier-status]')


      btn.classList.toggle('active', qty === highlighted)
      btn.classList.toggle('selected', isSelected)
      btn.classList.toggle('reached', applied !== null && qty <= applied)
      btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false')

      if (!status) return

      /* Only the tier in play says anything, and it says what to do next
         rather than restating that it is selected. */
      if (qty !== highlighted) {
        status.textContent = (applied !== null && qty <= applied) ? strings.applied || '' : ''
        return
      }

      const remaining = qty - this.bundleContents.length

      if (remaining > 0) {
        status.textContent = (strings.next || '').replace('[count]', remaining)
      } else if (remaining < 0) {
        status.textContent = (strings.remove || '').replace('[count]', Math.abs(remaining))
      } else {
        status.textContent = strings.applied || ''
      }
    })
  }

  /* Add/remove `delta` units of the card's selected variant. */
  changeQty (card, delta) {
    if (!card || !delta) return

    const option = this.selectedOption(card)
    const variantId = Number(option.dataset.variantId)

    if (delta > 0) {
      for (let i = 0; i < delta; i++) {
        this.bundleContents.push({
          product_id: Number(option.dataset.productId),
          product_title: option.dataset.productTitle,
          variant_id: variantId,
          variant_img: option.dataset.variantImg,
          variant_price: Number(option.dataset.variantPrice)
        })
      }
    } else {
      let remove = Math.abs(delta)

      for (let i = this.bundleContents.length - 1; i >= 0 && remove > 0; i--) {
        if (this.bundleContents[i].variant_id === variantId) {
          this.bundleContents.splice(i, 1)
          remove--
        }
      }
    }

    this.save()
    this.buildBundle()
    this.calcPrices()
  }

  /* Typed straight into the number input. */
  onQtyInput (input) {
    const card = input.closest('.product-card')
    const variantId = Number(this.selectedOption(card).dataset.variantId)
    const wanted = Math.max(0, Math.floor(Number(input.value) || 0))

    this.changeQty(card, wanted - this.qtyInBundle(variantId))
  }

  save () {
    localStorage.setItem('ks-bundle-contents', JSON.stringify(this.bundleContents))
  }

  /* Mirror the bundle back onto every product card's quantity selector. */
  syncProductCardQuantities () {
    this.querySelectorAll('.product-card').forEach(card => {
      const input = card.querySelector('[data-bundle-qty-input]')
      if (!input) return

      const qty = this.qtyInBundle(this.selectedOption(card).dataset.variantId)

      input.value = qty
      input.removeAttribute('max')

      /* Adding is never blocked — the top tier is the best discount, not a
         limit, so customers can keep going past it. */
      card.querySelectorAll('[data-bundle-qty-change]').forEach(btn => {
        btn.disabled = Number(btn.dataset.bundleQtyChange) < 0 && qty === 0
      })

      card.querySelector('[data-bundle-qty-selector]')?.classList.toggle('active', qty > 0)
    })
  }

  removeFromBundle (btn) {
    this.bundleContents = this.bundleContents.filter((elem, index) =>
      index !== Number(btn.dataset.number - 1))

    this.save()

    this.buildBundle()
    this.calcPrices()
  }

  /* Items added past the last tier still need a slot to show up in — and to
     be removable from — so extend the last tier's list to fit them. */
  renderExtraSlots (container) {
    container.querySelectorAll('.collection-bundle-card-list li.is-extra')
      .forEach(li => li.remove())

    const lists = container.querySelectorAll('.collection-bundle-card-list')
    const lastList = lists[lists.length - 1]
    if (!lastList) return

    const slots = container.querySelectorAll('.collection-bundle-card-list li').length
    const template = lastList.querySelector('li')
    if (!template) return

    for (let i = slots; i < this.bundleContents.length; i++) {
      const slot = template.cloneNode(true)
      const number = i + 1

      slot.classList.add('is-extra')
      slot.classList.remove('active')
      slot.querySelector('[data-number]').textContent = number
      slot.querySelector('[data-number]').setAttribute('data-number', number)
      slot.querySelector('.btn-close').setAttribute('data-number', number)
      lastList.appendChild(slot)
    }
  }

  buildBundle () {
    /* Slots are rendered once per container (sticky card + mobile popup), so
       index them within their own container instead of across the section. */
    this.querySelectorAll('[data-bundle-list-container]').forEach(container => {
      this.renderExtraSlots(container)

      container.querySelectorAll('.collection-bundle-card-list li').forEach((elem, index) => {
        const bundleElem = this.bundleContents[index]
        const img = elem.querySelector('img')

        if (bundleElem) {
          elem.classList.add('active')
          img.setAttribute('src', bundleElem.variant_img)
          img.setAttribute('alt', bundleElem.product_title)
          img.setAttribute('data-bs-toggle', 'tooltip')
          img.setAttribute('data-bs-placement', 'top')
          img.setAttribute('data-bs-title', bundleElem.product_title)
          elem.querySelector('.btn-close').setAttribute('data-variant-id', bundleElem.variant_id)
        } else {
          elem.classList.remove('active')
          img.setAttribute('src', img.dataset.src)
          img.setAttribute('alt', '')
          img.removeAttribute('data-bs-toggle')
          img.removeAttribute('data-bs-placement')
          img.removeAttribute('data-bs-title')
          elem.querySelector('.btn-close').removeAttribute('data-variant-id')
        }
      })
    })

    this.querySelectorAll('.collection-bundle-card-list-wrapper, [data-tier-summary]').forEach(elem => {
      if (Number(elem.dataset.tierQty) <= this.bundleContents.length) {
        elem.classList.add('active')
      } else {
        elem.classList.remove('active')
      }
    })

    /* With a tier selected, that tier has to be reached; otherwise any tier
       does, and going past one keeps checkout open. */
    const unlocked = this.selectedTier
      ? this.bundleContents.length >= this.selectedTier
      : this.appliedTier() !== null

    this.querySelectorAll('.btn-bundle-checkout').forEach(btn => { btn.disabled = !unlocked })

    this.updateTierSummary()
    this.syncProductCardQuantities()
  }

  calcPrices () {
    const totalPrice = this.bundleContents.reduce((accumulator, currentValue) =>
      currentValue.variant_price + accumulator, 0)

    const currentQty = this.bundleContents.length
    let discount = 0
    let savings = 0

    window.theme.bundle_builder_tiers.forEach(tier => {
      if (tier.qty <= currentQty) {
        discount = tier.discount
      }
    })

    if (discount > 0) {
      savings = discount / 100 * totalPrice
    }

    this.querySelectorAll('[data-bundle-price]').forEach(elem => {
      elem.textContent = window.Shopify.formatMoney(totalPrice)
    })

    this.querySelectorAll('[data-you-save]').forEach(elem => {
      elem.textContent = `${window.Shopify.formatMoney(savings)} (${discount}%)`
    })
  }

  handleCartStickyPosition () {
    if (window.matchMedia('(min-width: 800px)').matches) {
      const headerGroup = document.querySelector('#header-group.sticky-top')

      let oldScroll = window.scrollY

      window.addEventListener('scroll', () => {
        const newScroll = window.scrollY
        if (newScroll > oldScroll) {
          if (newScroll > headerGroup.clientHeight) {
            this.card.style.top = '20px'
          }
        } else if (newScroll < oldScroll) {
          this.card.style.top = `${headerGroup.clientHeight + 20}px`
        }

        oldScroll = Math.max(window.scrollY, 0)
      })
    }
  }

  onChangeProductCardVariant (select) {
    const variantImg = select[select.selectedIndex].dataset.variantImg

    if (variantImg.length) {
      select.closest('.product-card').querySelector('.product-card-img-wrapper img').setAttribute('src', variantImg)
    }

    this.syncProductCardQuantities()
  }

  animateBtnBundleCheckout () {
    const animate = () => {
      this.querySelectorAll('.btn-bundle-checkout').forEach(btn => {
        btn.classList.add('animate__animated', 'animate__shakeX')
      })
      setTimeout(() => {
        this.querySelectorAll('.btn-bundle-checkout').forEach(btn => {
          btn.classList.remove('animate__animated', 'animate__shakeX')
        })
      }, 1000)
    }
    animate()

    setTimeout(() => {
      setInterval(() => {
        animate()
      }, 4000)
    }, 1000)
  }

  async handleCheckout (btn) {
    btn.classList.add('loading')
    btn.disabled = true
    btn.setAttribute('aria-busy', 'true')

    const items = []

    this.bundleContents.forEach(elem => {
      items.push({
        id: elem.variant_id,
        quantity: 1
      })
    })

    if (btn.dataset.redirectToCheckout === 'true') {
      await fetch(`${window.Shopify.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      })
      window.location.href = `${window.Shopify.routes.root_url}checkout`
    } else {
      await document.querySelector('cart-container').add({ items })
    }

    btn.classList.remove('loading')
    btn.disabled = false
    btn.setAttribute('aria-busy', 'false')

    this.bundleContents = []
    this.selectedTier = null
    localStorage.removeItem('ks-bundle-selected-tier')
    this.save()
    this.buildBundle()
    this.calcPrices()
  }

  initStickyMobileCard () {
    const stickyCard = this.querySelector('#collection-bundle-sticky-mobile-card')
    if (!stickyCard) return

    const top = document.querySelector('#collection-bundle-card').getBoundingClientRect().bottom

    window.addEventListener('scroll', () => {
      if (window.matchMedia('(min-width: 800px').matches) return

      if (window.scrollY > top) {
        stickyCard.classList.add('show')
        document.body.style.paddingBottom = stickyCard.clientHeight + 'px'
      } else {
        stickyCard.classList.remove('show')
        document.body.style.paddingBottom = 0
      }
    })

    stickyCard.querySelector('.btn-scroll-bundle-card')
      ?.addEventListener('click', () => this.openPopup())
  }

  initMobilePopup () {
    this.popup = this.querySelector('[data-bundle-popup]')
    if (!this.popup) return

    this.popup.querySelectorAll('[data-bundle-popup-close]').forEach(elem => {
      elem.addEventListener('click', () => this.closePopup())
    })

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closePopup()
    })
  }

  openPopup () {
    if (!this.popup) return

    this.popup.classList.add('show')
    this.popup.setAttribute('aria-hidden', 'false')
    document.body.classList.add('bundle-popup-open')
  }

  closePopup () {
    if (!this.popup || !this.popup.classList.contains('show')) return

    this.popup.classList.remove('show')
    this.popup.setAttribute('aria-hidden', 'true')
    document.body.classList.remove('bundle-popup-open')
  }
}
customElements.define('bundle-builder', BundleBuilder)
